import { ChangeDetectorRef, Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewRef } from '@angular/core';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { TimeoutService } from '@df/ng/timeout.service';
// import { onInViewChangeEvent } from '@df/ui/common/on-in-view.directive';
import { IModelDataV1, Model } from '@df/catalog/model';
import { Resource } from '@df/catalog/resource';
import { Subscription } from 'rxjs';

export interface ISetModelEvent<T extends Model<IModelDataV1, string | number>> {
    model: T | undefined;
    promise?: Promise<T>;
}

export const setModelEvent = 'componentSetModel';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class AbstractItemComponent<T extends Model<IModelDataV1, string | number>> implements OnInit, OnChanges, OnDestroy {
    protected subscriptions: Subscription = new Subscription();

    /**
     * If can detach
     */
    protected canDetach: boolean | undefined;

    /**
     * @depreciated Please change to asyncCd (typo)
     */
    protected acyncCd = true;

    /**
     * If change detection can be async
     */
    protected asyncCd = true;

    /**
     * If partial model can be used
     */
    protected acceptPartialModel = false;

    /**
     * MOdel data change subscription
     */
    private modelSusbscription?: Subscription;

    /**
     * Default verbosity used when auto loading
     */
    verbosity = 2;

    initiated = false;

    /**
     * Model type id
     * @type {string}
     */
    abstract type: string;

    /**
     * Model id (usually set through binding)
     * @type {number}
     */
    @Input()
    modelId?: number | string;

    /**
     * Model url to load
     * @type {string}
     */
    @Input()
    modelUrl?: string;

    /**
     * Model setter - makes sure that its loaded with proper verbosity
     * @type {Model}
     */
    @Input()
    model?: T;

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        readonly app: App,
        protected timeoutService: TimeoutService
    ) {
        timeoutService.setTimeout(() => this.syncRef()); // delayed otherwise child contructor wont be able to initiate
    }

    /**
     * Access to client service.
     */
    get client(): Client {
        return this.app.client;
    }

    get events(): Events {
        return this.app.events;
    }

    private syncRef() {
        if (this.canDetach) {
            this.changeDetectorRef.detach();
        }
    }

    /**
     * Sets listtener for brakpoint changes
     */
    ngOnInit(): void {
        this.syncRef();
        this.initiated = true;
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.onBreakpoint()));
        // this causes double CD
        // this.subscriptions.add(this.events.on(onInViewChangeEvent)
        // .subscribe(this.timeoutService.debounce(() => this.detectChanges(), 20)));
        if (this.cdOnInit()) {
            this.detectChanges();
        }
    }

    protected onBreakpoint() {
        this.detectChanges();
    }

    protected cdOnInit(): boolean {
        return !!this.canDetach;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['modelUrl']) {
            this.setModelUrl(changes['modelUrl'].currentValue);
        }

        if (changes['modelId']) {
            this.setModelId(changes['modelId'].currentValue);
        }

        if (changes['model']) {
            this.setModel(changes['model'].currentValue);
            this.onModelChange(); // setModel will not trigger change event as the model is already assigned
        } else {
            this.detectChanges();
        }
    }

    /**
     * Cleanup
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    setModel(model: T | undefined): void {
        const modelVerbosity = model?.data?.verbosity;

        const eventData: ISetModelEvent<T> = {
            model
        };

        const next = model => {
            if (model && (!modelVerbosity || modelVerbosity < this.verbosity)) {
                if (this.acceptPartialModel) {
                    this._setModel(model);
                }
                // if verbosity already on rquested level then bind data immediatly
                model
                    .load(this.verbosity)
                    .then(() => this._setModel(model))
                    .catch(() => {
                        this.onError(model);
                    });
            } else {
                this._setModel(model);
            }
        };

        this.app.events.broadcast(setModelEvent, eventData);

        if (eventData.promise) {
            eventData.promise.then(model => next(model));
        } else {
            next(eventData.model);
        }
    }

    protected setModelUrl(url?: string): void {
        if (url && !this.modelId && !(this.model?.data && this.model.data['url'] === url)) {
            let loaded = false;

            const resource = this.getResource();
            const matchedModel = resource.matchOne({ url });

            if (matchedModel) {
                loaded = true;

                this.setModel(matchedModel);
            } else {
                this.setModel(undefined);

                resource
                    .findOne({
                        filter: { url },
                        verbosity: this.verbosity
                    })
                    .then(model => {
                        loaded = true;

                        if (!this.modelId) {
                            this.setModel(model || undefined);
                        }
                    })
                    .catch(() => {
                        if (!loaded) {
                            this.onError();
                        }
                    });
            }
        }
    }

    protected setModelId(id?: number | string) {
        if (id && this.model?.id !== id) {
            this.setModel(this.getResource().getCreate(id));
        }
    }

    /**
     * Resource getter shorthand
     * @return {Resource<T>}
     */
    getResource(): Resource<T> {
        return this.app.resources[this.type] as any as Resource<T>;
    }

    detectChanges(): void {
        if (this.initiated) {
            const cd = () => {
                if (!(<ViewRef>this.changeDetectorRef).destroyed) {
                    this.changeDetectorRef.detectChanges();
                }
            };
            if (this.acyncCd && this.asyncCd) {
                this.timeoutService.async(cd);
            } else {
                cd();
            }
        }
    }

    /**
     * Function deploying model in component
     * @param {T | undefined} model [description]
     */
    protected _setModel(model: T | undefined): void {
        if (this.model !== model) {
            this.model = model;
            this.onModelChange();
        } else {
            this.resetModelSubscription();
        }
    }

    /**
     * Called on load err
     */
    protected onError(model?: T): void {}

    /**
     * On model change hook
     */
    protected onModelChange(): void {
        this.resetModelSubscription();
        this.detectChanges();
    }

    private resetModelSubscription() {
        this.modelSusbscription?.unsubscribe();
        this.modelSusbscription = this.model?.change.subscribe(() => this.onModelDataChange());
    }

    protected onModelDataChange(): void {
        this.detectChanges();
    }
}
